<template>
    <div class="relative">
        <h1 class="font-body-6 text-default-2 xxs:pl-4 lg:pl-0">System notifications</h1>
        <div class="xxs:p-4 bg-white rounded border border-white lg:px-6 lg:pt-4 lg:pb-6 mt-4 box-border">
            <div v-if="loadingPage" class="w-full flex flex-col box-border">
                <div class="w-full flex xxs:flex-col lg:flex-row mb-5">
                    <div class="skeleton-loader xxs:w-full lg:w-2/5 h-[42px]"></div>
                    <div class="skeleton-loader xxs:w-full md:w-full lg:w-1/4 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    <div class="flex-1 flex">
                        <div
                            class="skeleton-loader xxs:w-[49%] lg:w-[20%] h-[42px] xxs:ml-0 xxs:mt-4 lg:mt-0 lg:ml-4 xxs:mr-4"></div>
                        <div class="skeleton-loader flex-1 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    </div>
                </div>
                <div v-for="item in 10 - 1" :key="item" class="skeleton-loader w-full mt-1 h-[68px]"></div>
                <div class="flex justify-between w-full mt-6">
                    <div v-for="i in 3" :key="i" class="w-1/5 skeleton-loader h-[42px]"></div>
                </div>
            </div>
            <div v-else class="w-full flex flex-col">
                <div v-if="hasNotifications" class="w-full flex xxs:items-end xxs:flex-wrap md:justify-center md:items-start xl:flex-nowrap">
                    <CoreInput
                        v-model="search"
                        class="xxs:w-full md:w-full lg:w-[40%]"
                        hasSearchIcon
                        label="Search"
                        placeholder="Enter a keyword to search for a notification"
                        @input="handleSearch"
                    >
                        <div
                            class="absolute inset-y-0 right-0 pr-3 flex items-center h-10 cursor-pointer">
                            <img v-if="!search" alt="Search through notifications" src="@/assets/icons/search.svg"/>
                            <img v-else src="@/assets/icons/close-light.svg" @click="clearSearchInput" alt="clear"/>
                        </div>
                    </CoreInput>
                  <TagSelect
                            class="xxs:w-full xxs:mt-3 md:w-1/2 lg:ml-4 lg:mt-0 lg:w-[25%]"
                            label="Notification type"
                             placeholder="Not selected"
                             :options="types"
                             v-model="selectedType"
                             selectValueProp="value"
                             noResultsText="No type found"
                             closeOnSelect
                             select-label="label"
                             select-value-prop="value"
                             @update:modelValue="handleTypeChange"
                             mode="multiple"
                  />
                    <div class="xxs:flex flex-1"></div>
                </div>
                <div v-if="notifications.length > 0" class="w-full pl-3 mt-6 mb-3 flex">
                    <Checkbox :modelValue="selectedAll" @update:modelValue="toggleSelectAll" label="Select all"/>
                </div>
                <div v-if="loading" class="w-full flex flex-col box-border">
                    <div v-for="item in 10 - 1" :key="item" class="skeleton-loader w-full mt-1 h-[67px]"></div>
                </div>
                <div v-if="!loading && notifications.length > 0" class="flex w-full flex-col">
                    <NotificationItem
                        v-for="notification in notifications"
                        :key="notification.uid"
                        :notification="notification"
                        :selectedNotifications="selectedNotifications"
                        hasCheckbox
                        @deleted="fetchItems"
                        @clicked="handleNotificationClick(notification)"
                        @update:modelValue="selectNotification"
                    />
                </div>
                <div v-if="notifications.length === 0" class="w-full h-[650px] flex justify-center items-center flex-col">
                    <NotificationsIcon :color="color.hex" />
                    <p class="mt-6 text-body-5">{{ emptyStateText }}</p>
                </div>
                <div class="flex w-full flex-col">
                    <Pagination
                        v-if="!loadingPage && hasNotifications"
                        :from="meta.from ?? 0"
                        :last-page="meta.last_page"
                        :links="links"
                        :to="meta.to ?? 0"
                        :total="meta.total ?? 0"
                        @change="handlePaginationChange"
                    />
                </div>
            </div>
        </div>
        <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
            <div v-if="selectedNotifications.length > 0" class="flex bg-white fixed left-1/2 bottom-6 xxs:-translate-x-[50%] lg:-translate-x-[35%] rounded-md">
                <div class="xxs:min-w-[calc(50vw-16px)] lg:min-w-[260px] flex justify-center px-6 py-4 box-border cursor-pointer bg-primary-1 hover:opacity-90 rounded-l-md" @click="markSelectedAsRead">
                    <div v-if="!loadingMarkAsRead" class="flex justify-center xxs:flex-col xxs:items-center xxs:text-center lg:flex-row">
                        <img alt="" class="w-5 mr-2 xxs:mb-3 lg:mb-0" src="@/assets/icons/double-check-white.svg"/>
                        <span class="text-white text-body-6">{{ markAsReadLabel }}</span>
                    </div>
                    <img v-else alt="" src="@/assets/icons/white-spinner.svg" />
                </div>
                <div
                    class="xxs:min-w-[calc(50vw-16px)] lg:min-w-[260px] flex justify-center px-6 py-4 box-border border-l-[1px] border-white/30 cursor-pointer bg-primary-1 hover:opacity-90 rounded-r-md"
                    @click="deleteSelectedNotifications"
                >
                    <div v-if="!loadingDelete" class="flex justify-center xxs:flex-col xxs:items-center xxs:text-center lg:flex-row">
                        <img alt="" class="w-5 mr-2 xxs:mb-3 lg:mb-0" src="@/assets/icons/trash.svg"/>
                        <span class="text-white text-body-6">{{ deleteLabel }}</span>
                    </div>
                    <img v-else alt="" src="@/assets/icons/white-spinner.svg" />
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import CoreInput from "@/components/core/InputGroup";
import NotificationItem from "@/components/sections/notifications/Item.vue";
import Checkbox from "@/components/core/Checkbox.vue";
import Pagination from "@/components/core/Pagination.vue";
import {onBeforeRouteUpdate, useRouter} from 'vue-router';
import {ref, computed, onBeforeMount} from "vue";
import {useAuth} from "@/stores/AuthStore";
import {useNotifications} from "@/composables/useNotifications";
import debounce from "lodash.debounce"
import TagSelect from "@/components/core/Select/TagSelect.vue";
import {toast} from "vue3-toastify";
import NotificationsIcon from '@/components/icons/Notifications.vue'
import {useTheme} from "@/stores/ThemeStore";
import {storeToRefs} from "pinia";
const {color} = storeToRefs(useTheme())

const router = useRouter();
const {member, organization} = useAuth();
const {getNotifications, getNotificationsTypes, readNotification, deleteNotification} = useNotifications();

const loadingPage = ref(false);
const loading = ref(false);
const notifications = ref([]);
const types = ref([]);
const selectedType = ref([]);
const search = ref('');
const meta = ref(null);
const links = ref([]);
const selectedNotifications = ref([]);
const selectedAll = ref(false);

const loadingMarkAsRead = ref(false);
const loadingDelete = ref(false);

const hasNotifications = ref(false);

const emptyStateText = computed(() => {
    return hasNotifications.value ? 'We couldn’t find any notifications based on the searched filters' : 'You do not have any notifications';
});

const fetchNotifications = async (page, perPage, search = '', types = []) => {
    loading.value = true;

    let response = await getNotifications(
        organization.slug,
        member.uid,
        page,
        perPage,
        search,
        types
    );

    notifications.value = response.data;
    links.value = response.links;
    meta.value = response.meta;

    loading.value = false;

    return response;
}

onBeforeMount(async () => {
    loadingPage.value = true;

    fetchNotifications(1, 10, '', []).then((response) => {
        hasNotifications.value = response.data.length > 0;
    }).finally(() => {
        loadingPage.value = false;
    });

    getNotificationsTypes(organization.slug).then((response) => {
        types.value = response.data.map(item => {
            let label = item.name.split('-').join(' ')
            return {
                label: label,
                value: item.name
            }
        })
    });
});

const debounceSearch = debounce(async (page = 1, perPage = 10) => {
    await fetchNotifications(page, perPage, search.value, selectedType.value);
}, 300);


const handlePaginationChange = ({page, perPage}) => {
    selectedAll.value = false;
    selectedNotifications.value = [];
    debounceSearch(page, perPage);
}

const handleSearch = () => {
    selectedAll.value = false;
    selectedNotifications.value = [];
    debounceSearch();
}

const clearSearchInput = () => {
    search.value = '';
    handleSearch();
}

const handleTypeChange = (types) => {
    selectedType.value = types;
    selectedAll.value = false;
    selectedNotifications.value = [];
    debounceSearch(1, meta.value.per_page);
}

function toggleSelectAll(event) {
    selectedAll.value = !selectedAll.value;
    if (event) {
        selectedNotifications.value = notifications.value.map(item => {
            return item.uid;
        });
    } else {
        selectedNotifications.value = [];
    }
}

function clearSelectedNotifications() {
    selectedNotifications.value = [];
    selectedAll.value = false;
}

function selectNotification(event) {
    const index = selectedNotifications.value.indexOf(event);
    if (index > -1) {
        selectedNotifications.value.splice(index, 1);
        return;
    }
    selectedNotifications.value.push(event)
}

onBeforeRouteUpdate(async (to, from) => {
    if (to.params.id !== from.params.id) {
        clearSelectedNotifications();
        //fetchNewNotifications
    }
});

const markAsReadLabel = computed(() => {
    const notificationCount = selectedNotifications.value.length;
    const notification = notificationCount > 1 ? 'notifications' : 'notification';
    return `Mark ${notificationCount} ${notification} as read`;
});

const successMarkAsReadLabel = computed(() => {
    const notificationCount = selectedNotifications.value.length;
    const notification = notificationCount > 1 ? 'notifications' : 'notification';
    return `${notificationCount} ${notification} marked as read`;
});

const deleteLabel = computed(() => {
    const notificationCount = selectedNotifications.value.length;
    const notification = notificationCount > 1 ? 'notifications' : 'notification';
    return `Delete ${notificationCount} ${notification}`;
})

const successDeleteLabel = computed(() => {
    const notificationCount = selectedNotifications.value.length;
    const notification = notificationCount > 1 ? 'notifications' : 'notification';
    return `${notificationCount} ${notification} deleted`;
})

function markSelectedAsRead() {
    loadingMarkAsRead.value = true;
    Promise.all(selectedNotifications.value.map(item => {
        let index = notifications.value.findIndex(notification => notification.uid === item);
        if(!notifications.value[index].read_at) {
            return readNotification(organization.slug, member.uid, item);
        }
    })).then(responses => {
        responses.forEach(response => {
            if(response) {
                let index = notifications.value.findIndex(notification => {
                    return notification.uid === response.uid
                });
                notifications.value[index].read_at = response.read_at;
            }
        })
        toast.success(successMarkAsReadLabel.value);
    }).finally(() => {
        selectedNotifications.value = [];
        selectedAll.value = false;
        loadingMarkAsRead.value = false;
    })
}

async function handleNotificationClick(notification) {
    if(notification.read_at === null) {
        await readNotification(organization.slug, member.uid, notification.uid);
    }

    if (notification.go_to) {
        await router.push(notification.go_to);
    }
}

function deleteSelectedNotifications() {
    loadingDelete.value = true;
    Promise.all(selectedNotifications.value.map(item => {
        return deleteNotification(organization.slug, member.uid, item);
    })).then(() => {
        search.value = '';
        selectedType.value = [];
        fetchItems();
        toast.success(successDeleteLabel.value);
    }).finally(() => {
        selectedNotifications.value = [];
        loadingDelete.value = false;
        selectedAll.value = false;
    })
}

function fetchItems() {
    fetchNotifications(meta.value.current_page, meta.value.per_page, search.value, selectedType.value).then(() => {
        hasNotifications.value = notifications.value.length > 0;
    })
}
</script>
